import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import {
  BatteryCharging,
  Gauge,
  TrendingDown,
  Zap,
  AlertTriangle,
  Wifi,
  Plus,
  Minus,
  Sparkles,
} from "lucide-react";
import {
  DEMO_CHARGE_EFFICIENCY,
  DEMO_RECENT_SESSIONS,
} from "../lib/demoData";
import { useVehicle } from "../lib/vehicleContext";
import { VehiclePicker } from "../components/VehiclePicker";

// Brand accent themes — the demo frame, gauge and charts tint themselves to
// match the selected sample vehicle's marque.
const BRAND_THEMES = {
  tesla: {
    label: "Tesla",
    accent: "#39FF14",
    accent2: "#00E5FF",
    frameTint: "transparent",
    glow: "drop-shadow(0 0 12px rgba(57,255,20,0.45)) drop-shadow(0 0 20px rgba(0,229,255,0.25))",
  },
  ford: {
    label: "Ford",
    accent: "#2E7CF6",
    accent2: "#7FB3FF",
    frameTint: "rgba(46,124,246,0.07)",
    glow: "drop-shadow(0 0 12px rgba(46,124,246,0.5)) drop-shadow(0 0 20px rgba(127,179,255,0.3))",
  },
  volvo: {
    label: "Volvo · Polestar",
    accent: "#C9D3DE",
    accent2: "#8FA3B8",
    frameTint: "rgba(201,211,222,0.055)",
    glow: "drop-shadow(0 0 12px rgba(201,211,222,0.4)) drop-shadow(0 0 20px rgba(143,163,184,0.25))",
  },
};
const themeFor = (brand) => BRAND_THEMES[brand] || BRAND_THEMES.tesla;

// Preview vehicle card — used when the selected vehicle is an unreleased model.
// Renders a collapsed "+" header by default; click to expand and reveal the
// announced spec sheet.
const PreviewVehicleCard = ({ vehicle }) => {
  const [open, setOpen] = useState(false);
  const a = vehicle.announced || {};
  return (
    <div
      className="gradient-border p-[1px] rounded-3xl"
      data-testid="dashboard-preview-card"
    >
      <div className="bg-[#0a0a0a] rounded-3xl overflow-hidden">
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="preview-card-toggle"
          aria-expanded={open}
          className="w-full px-6 py-5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors text-left"
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(255,176,32,0.08)" }}
          >
            <Sparkles
              className="w-4 h-4"
              style={{ color: "#FFB020" }}
              strokeWidth={1.8}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <div className="text-sm text-white font-medium truncate">
                {vehicle.year} {vehicle.name}
              </div>
              <span
                className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border"
                style={{
                  color: "#FFB020",
                  borderColor: "rgba(255,176,32,0.3)",
                  background: "rgba(255,176,32,0.06)",
                }}
              >
                Preview · unreleased
              </span>
            </div>
            <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
              Live telemetry will be available at delivery — tap to view
              announced specs
            </div>
          </div>
          <div
            className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center shrink-0 hover:border-white/30 transition-colors"
            aria-hidden="true"
          >
            {open ? (
              <Minus className="w-4 h-4 text-white" strokeWidth={2} />
            ) : (
              <Plus className="w-4 h-4 text-white" strokeWidth={2} />
            )}
          </div>
        </button>

        {open && (
          <div
            className="border-t border-white/5 px-6 py-6"
            data-testid="preview-card-body"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
              {Object.entries({
                "EPA range (claimed)": `${vehicle.epaRange} mi`,
                "Pack capacity": a.packKWh || "—",
                "0–60 mph": a.zeroToSixty || "—",
                "Top speed": a.topSpeed || "—",
                "Cell chemistry": a.chemistry || "—",
                "Starting price": a.price || "—",
              }).map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-3"
                >
                  <span className="text-xs uppercase tracking-wider text-zinc-500 font-mono">
                    {k}
                  </span>
                  <span className="font-mono text-sm text-white text-right">
                    {v}
                  </span>
                </div>
              ))}
            </div>

            <div
              className="mt-6 rounded-xl border border-white/10 bg-black/40 p-4 flex items-start gap-3"
              data-testid="preview-warranty-note"
            >
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "rgba(57,255,20,0.08)" }}
              >
                <BatteryCharging
                  className="w-3.5 h-3.5"
                  style={{ color: "#39FF14" }}
                  strokeWidth={1.8}
                />
              </div>
              <div className="text-xs text-zinc-300 leading-relaxed">
                <span className="text-white font-medium">
                  Estimated warranty:
                </span>{" "}
                {vehicle.warranty.years} yrs /{" "}
                {vehicle.warranty.miles.toLocaleString()} mi · {vehicle.warranty.threshold}%
                retention (est. — final terms TBD at delivery). EVIQ will
                light up the full live dashboard the moment your VIN is
                provisioned.
              </div>
            </div>

            <div className="mt-5 text-[11px] font-mono text-zinc-500 leading-relaxed">
              {a.delivery}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Circular health gauge (SVG). When `floor` is null/undefined, the orange
// warranty-threshold marker is hidden — used for vehicles with no published
// retention guarantee (e.g. the original Roadster).
const HealthGauge = ({ value, floor, theme }) => {
  const size = 220;
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  const arcFraction = 0.75;
  const arcLength = c * arcFraction;
  const filled = (value / 100) * arcLength;
  const gradId = `gaugeGradient-${theme.label.replace(/[^a-z]/gi, "")}`;

  return (
    <div className="relative flex items-center justify-center" data-testid="health-gauge">
      <svg width={size} height={size} className="-rotate-[225deg]">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={theme.accent} />
            <stop offset="100%" stopColor={theme.accent2} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
          strokeDasharray={`${arcLength} ${c}`}
          strokeLinecap="round"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#gaugeGradient-${theme.label.replace(/[^a-z]/gi, "")})`}
          strokeWidth={stroke}
          strokeDasharray={`${filled} ${c}`}
          strokeLinecap="round"
          style={{
            filter: theme.glow,
            transition: "stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
        {floor && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#FFB020"
            strokeWidth={stroke + 2}
            strokeDasharray={`2 ${c}`}
            strokeDashoffset={-((floor / 100) * arcLength)}
            strokeLinecap="round"
            opacity={0.8}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-500">
          EVIQ Estimated SoH
        </div>
        <div
          className="font-display text-6xl text-white font-semibold mt-1"
          style={{ textShadow: `0 0 24px ${theme.accent}59` }}
          data-testid="dashboard-soh-value"
        >
          {value}
          <span className="text-2xl text-zinc-500 font-mono ml-0.5">%</span>
        </div>
        <div className="text-[11px] font-mono text-zinc-500 mt-1">
          {floor ? `Warranty floor · ${floor}%` : "No published floor"}
        </div>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 rounded-full text-xs font-medium font-mono tracking-wider uppercase transition-all ${
      active
        ? "bg-white text-black"
        : "text-zinc-400 hover:text-white border border-white/10"
    }`}
  >
    {children}
  </button>
);

export const DemoDashboard = () => {
  const { vehicle, degradation } = useVehicle();
  const [tab, setTab] = useState("degradation");
  const theme = themeFor(vehicle.brand);

  const headroom = useMemo(
    () => (vehicle.sohCurrent - vehicle.warranty.threshold).toFixed(1),
    [vehicle]
  );

  // Projection: months to reach warranty floor based on current 30d delta
  const monthsToFloor = useMemo(() => {
    if (!vehicle.warranty.threshold) return null;
    const monthlyLoss = Math.abs(vehicle.delta30d || 0.1);
    return Math.round((vehicle.sohCurrent - vehicle.warranty.threshold) / monthlyLoss);
  }, [vehicle]);

  // Months remaining in warranty (vs vehicle year)
  const warrantyMonthsLeft = useMemo(() => {
    const ageMonths = (new Date().getFullYear() - vehicle.year) * 12;
    return Math.max(0, vehicle.warranty.years * 12 - ageMonths);
  }, [vehicle]);

  const rangeLost = Math.max(0, vehicle.epaRange - vehicle.currentRange);

  return (
    <section
      id="demo"
      data-testid="demo-section"
      aria-label="EVIQ demo dashboard — simulated data showing live State of Health, degradation curve, charging efficiency, and warranty headroom for sample Tesla, Ford, and Volvo/Polestar vehicles (for example a 2021 Model 3 Long Range at 89.4% State of Health)"
      className="relative py-16 sm:py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 sm:mb-10">
          <div>
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
              [ 03 — Live demo · simulated data ]
            </div>
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95] text-white">
              This is what your
              <br />
              <span className="brand-text-gradient">battery looks like</span> to
              us.
            </h2>
          </div>
          <p className="text-zinc-400 lg:max-w-sm text-sm sm:text-base leading-relaxed">
            Switch between sample Tesla, Ford &amp; Volvo/Polestar configurations to see how
            EVIQ adapts — from a 2024 Cybertruck to a Polestar 2.
          </p>
        </div>

        {/* Vehicle Picker row */}
        <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            Pick a sample vehicle →
          </div>
          <VehiclePicker />
        </div>

        {vehicle.isPreview ? (
          <PreviewVehicleCard vehicle={vehicle} />
        ) : (
        /* Dashboard frame */
        <div
          className="gradient-border p-[1px] rounded-3xl"
          data-testid="dashboard-frame"
        >
          <div
            className="bg-[#0a0a0a] rounded-3xl overflow-hidden transition-colors duration-700"
            style={{ backgroundImage: `linear-gradient(180deg, ${theme.frameTint}, transparent 55%)` }}
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/5 gap-3">
              <div className="flex items-center gap-3 min-w-0" data-testid="dashboard-vehicle-header">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-700"
                  style={{ background: `${theme.accent}14` }}
                >
                  <BatteryCharging className="w-4 h-4 transition-colors duration-700" style={{ color: theme.accent }} strokeWidth={1.8} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium truncate">
                    {/* Marketing showcase — static demo identity so a
                        signed-in owner's real car name/VIN never leaks
                        into this landing-page section. */}
                    {vehicle.year} {vehicle.name} · Sample Owner
                  </div>
                  <div className="font-mono text-[11px] text-zinc-500 truncate">
                    VIN {vehicle.vin} · {vehicle.odometer.toLocaleString()} mi · {vehicle.color}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border transition-colors duration-700"
                  data-testid="demo-brand-badge"
                  style={{ color: theme.accent, borderColor: `${theme.accent}4D`, background: `${theme.accent}0F` }}
                >
                  {theme.label}
                </span>
                <div className="hidden sm:flex items-center gap-2 font-mono text-[11px] text-zinc-400">
                  <Wifi className="w-3.5 h-3.5" style={{ color: theme.accent }} strokeWidth={2} />
                  <span>Synced 2 min ago</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/5">
              {/* Health gauge column */}
              <div className="lg:col-span-4 bg-[#0a0a0a] p-6 sm:p-8 flex flex-col items-center justify-center">
                <HealthGauge
                  value={vehicle.sohCurrent}
                  floor={vehicle.warranty.threshold}
                  theme={theme}
                />
                <div className="mt-6 w-full grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-white/5 px-3 py-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                      Headroom
                    </div>
                    <div
                      className="font-mono text-lg text-white"
                      style={{ color: parseFloat(headroom) < 5 ? "#FF453A" : "#fff" }}
                    >
                      {parseFloat(headroom) >= 0 ? "+" : ""}{headroom}%
                    </div>
                  </div>
                  <div className="rounded-lg border border-white/5 px-3 py-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                      30d Δ
                    </div>
                    <div
                      className="font-mono text-lg"
                      style={{ color: Math.abs(vehicle.delta30d) > 0.3 ? "#FFB020" : "#39FF14" }}
                    >
                      {vehicle.delta30d}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Metrics + chart */}
              <div className="lg:col-span-8 bg-[#0a0a0a] p-4 sm:p-6 lg:p-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <MetricTile
                    icon={Gauge}
                    label="Range at 100%"
                    value={`${vehicle.currentRange} mi`}
                    sub={`EPA ${vehicle.epaRange} mi`}
                  />
                  <MetricTile
                    icon={TrendingDown}
                    label="Range lost"
                    value={`${rangeLost} mi`}
                    sub="since new"
                    color="#FFB020"
                  />
                  <MetricTile
                    icon={Zap}
                    label="Last session"
                    value="89%"
                    sub="efficiency"
                  />
                  <MetricTile
                    icon={BatteryCharging}
                    label="Fleet avg"
                    value={`${vehicle.fleetAvg}%`}
                    sub="same model"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <TabButton
                    active={tab === "degradation"}
                    onClick={() => setTab("degradation")}
                    testid="tab-degradation"
                  >
                    Degradation
                  </TabButton>
                  <TabButton
                    active={tab === "efficiency"}
                    onClick={() => setTab("efficiency")}
                    testid="tab-efficiency"
                  >
                    Charging efficiency
                  </TabButton>
                  <TabButton
                    active={tab === "sessions"}
                    onClick={() => setTab("sessions")}
                    testid="tab-sessions"
                  >
                    Recent sessions
                  </TabButton>
                </div>

                <div className="h-[280px] w-full">
                  {tab === "degradation" && (
                    <DegradationChart
                      data={degradation}
                      floor={vehicle.warranty.threshold}
                      theme={theme}
                    />
                  )}
                  {tab === "efficiency" && <EfficiencyChart />}
                  {tab === "sessions" && <SessionsTable />}
                </div>
              </div>
            </div>

            {/* Alert strip */}
            <div className="px-4 sm:px-6 py-4 border-t border-white/5 bg-[#080808] flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "rgba(255,176,32,0.1)" }}
              >
                <AlertTriangle className="w-4 h-4" style={{ color: "#FFB020" }} strokeWidth={2} />
              </div>
              <div className="flex-1">
                <div className="text-sm text-white font-medium" data-testid="dashboard-alert-strip">
                  Trend nominal — projected to cross{" "}
                  {vehicle.warranty.threshold}% in{" "}
                  <span className="font-mono text-white">
                    ~{monthsToFloor} months
                  </span>
                </div>
                <div className="text-xs text-zinc-500 font-mono">
                  Warranty: {vehicle.warranty.years} yrs /{" "}
                  {vehicle.warranty.miles.toLocaleString()} mi · ~
                  {warrantyMonthsLeft} months left by age
                </div>
              </div>
              <span className="hidden sm:inline-block font-mono text-[10px] uppercase tracking-wider text-zinc-500 px-2 py-1 rounded border border-white/10">
                Demo data
              </span>
            </div>
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

const MetricTile = ({ icon: Icon, label, value, sub, color }) => (
  <div className="rounded-xl border border-white/5 bg-black/40 p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon
        className="w-3.5 h-3.5 text-zinc-500"
        strokeWidth={1.8}
        style={color ? { color } : undefined}
      />
      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
        {label}
      </span>
    </div>
    <div
      className="font-mono text-2xl font-medium leading-none"
      style={{ color: color || "#fff" }}
    >
      {value}
    </div>
    <div className="text-[11px] text-zinc-500 mt-1.5 font-mono">{sub}</div>
  </div>
);

const DegradationChart = ({ data, floor, theme }) => {
  const minY = Math.min(...data.map((d) => d.soh)) - 5;
  const gid = theme.label.replace(/[^a-z]/gi, "");
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 8, bottom: 0, left: -10 }}>
        <defs>
          <linearGradient id={`sohFill-${gid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.accent} stopOpacity={0.35} />
            <stop offset="100%" stopColor={theme.accent2} stopOpacity={0} />
          </linearGradient>
          <linearGradient id={`sohStroke-${gid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={theme.accent} />
            <stop offset="100%" stopColor={theme.accent2} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#222" strokeDasharray="3 4" vertical={false} />
        <XAxis dataKey="month" stroke="#555" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
        <YAxis domain={[Math.max(60, minY), 100]} stroke="#555" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
        <Tooltip />
        {floor && (
          <ReferenceLine
            y={floor}
            stroke="#FFB020"
            strokeDasharray="4 4"
            label={{
              value: `${floor}% floor`,
              position: "insideTopLeft",
              fill: "#FFB020",
              fontSize: 10,
              fontFamily: "JetBrains Mono",
            }}
          />
        )}
        <Area type="monotone" dataKey="fleet" stroke="#71717A" strokeWidth={1.5} strokeDasharray="4 4" fill="transparent" />
        <Area type="monotone" dataKey="soh" stroke={`url(#sohStroke-${gid})`} strokeWidth={2.5} fill={`url(#sohFill-${gid})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const EfficiencyChart = () => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={DEMO_CHARGE_EFFICIENCY} margin={{ top: 10, right: 8, bottom: 0, left: -10 }}>
      <CartesianGrid stroke="#222" strokeDasharray="3 4" vertical={false} />
      <XAxis dataKey="day" stroke="#555" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
      <YAxis domain={[80, 100]} stroke="#555" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
      <Tooltip />
      <Legend
        verticalAlign="top"
        wrapperStyle={{ fontSize: "11px", fontFamily: "JetBrains Mono", color: "#A1A1AA" }}
        iconType="circle"
      />
      <Bar dataKey="supercharger" name="Supercharger" fill="#00E5FF" radius={[4, 4, 0, 0]} maxBarSize={18} />
      <Bar dataKey="home" name="Home AC" fill="#39FF14" radius={[4, 4, 0, 0]} maxBarSize={18} />
    </BarChart>
  </ResponsiveContainer>
);

const SessionsTable = () => (
  <div className="h-full overflow-auto" data-testid="sessions-table">
    <table className="w-full">
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-white/5">
          <th className="text-left font-normal py-3 px-2">When</th>
          <th className="text-left font-normal py-3 px-2">Type</th>
          <th className="text-right font-normal py-3 px-2">kWh</th>
          <th className="text-right font-normal py-3 px-2">SoC</th>
          <th className="text-right font-normal py-3 px-2">Eff.</th>
        </tr>
      </thead>
      <tbody>
        {DEMO_RECENT_SESSIONS.map((s, i) => (
          <tr
            key={i}
            className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
          >
            <td className="py-3 px-2 text-sm text-white">{s.time}</td>
            <td className="py-3 px-2 text-sm text-zinc-400 font-mono">{s.type}</td>
            <td className="py-3 px-2 text-right text-sm text-white font-mono">
              {s.kwh}
            </td>
            <td className="py-3 px-2 text-right text-sm text-zinc-400 font-mono">
              {s.soc}
            </td>
            <td className="py-3 px-2 text-right text-sm font-mono"
                style={{ color: s.efficiency >= 90 ? "#39FF14" : "#FFB020" }}>
              {s.efficiency}%
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
